import $ from 'jquery';
import { doXhr } from '../../remote/request.js';
import { showCartModal } from '../../shop/modules/header-actions-entities/instance-builders/cart-modal.js';
import { showCartDrawer } from '../../shop/modules/header-actions-entities/instance-builders/cart-drawer.js';
import cartDropdownInit from '../../shop/modules/header-actions-entities/instance-builders/cart-dropdown.js';
import { parseDataAction } from '../../util/data.js';

/**
 * @class ShopActionError
 */
class ShopActionError extends Error {

	static get NAME() {
		return 'ShopActionError';
	}

	/**
	 * @param {{ status: any; error: any; message: any; }} obj
	 */
	constructor(obj) {
		super(obj.message);
		this.status = obj.status;
		this.error = obj.error;
		// @ts-ignore
		// eslint-disable-next-line unicorn/custom-error-definition
		this.name = this.constructor.NAME;
	}
}

const parseToastOptions = (data = {}) => {

	// Args format
	if (Array.isArray(data)) {
		const [format, content, options] = data;
		if (options) {
			return { format, content, ...options };
		}
		if (content) {
			return { format, ...content };
		}
		return format;
	}

	// Error object
	if (data instanceof Error) {
		return { format: 'error', content: data.message };
	}

	// Message object or string
	if (data.message) {
		return {
			// ? Note: the message object has the deprecated 'type' instead of 'format'
			format: data.message.format ?? data.message.type ?? 'success',
			...(data.message.content ? data.message : { content: data.message }),
		};
	}

	// Full config object
	return data;
};

const appendTimeToUrl = (url) => {
	const parsedUrl = new URL(url);
	parsedUrl.searchParams.append('v', Date.now().toString());

	return parsedUrl.toString();
};

mpro.actionHandlers = {

	/**
	 * Create a setCookie actionHandler
	 *
	 * @param {string|{[key: string]: any;}} name
	 * @param {string|object=} value
	 * @param {{[key: string]: any;}=} options
	 */
	setCookie(name, value, options) {

		// Allow to pass an object as first argument (this takes care of the actionHandler required format with args as the first argument)
		if (typeof name === 'object') {

			options = name;
			({ name, value } = options);

			delete options.name;
			delete options.value;
		}

		if (value === undefined) {
			value = '1';
		}
		// @ts-ignore
		else if (value === 0) {
			value = '0';
		}

		// Remove cookie
		if (!value) {
			// @ts-ignore
			mpro.Cookies.remove(name);
			return;
		}

		// Use the provided options
		if (options && (typeof options === 'object')) {

			// Normalize the expires value - most of the time the expires comes from php which is expressed in seconds
			// Must convert it in milliseconds
			if (typeof options.expires !== 'undefined') {
				options.expires = new Date((Number(options.expires).toString() === options.expires) ? (Number(options.expires) * 1000) : options.expires);
			}

			// Merge object/array or append string to current value
			if (options.mergeValue || (options.appendWith != null)) {

				// Get current cookie value
				let currentVal;

				try {
					// @ts-ignore
					currentVal = JSON.parse(mpro.Cookies.get(name));
				}
				catch {
					currentVal = '';
				}

				// Merge object
				if (options.mergeValue && currentVal) {
					// Merge array
					if (Array.isArray(currentVal)) {
						// @ts-ignore
						value = currentVal.concat(Array.isArray(value) ? value : [value]);
					}
					// Merge object
					else if (typeof currentVal === 'object') {
						value = Array.isArray(value) ? (typeof value === 'object' ? value : [value]) : [value];
						value = { ...currentVal, ...value };
					}
				}
				// Append string
				else if (options.appendWith != null) {
					value = currentVal + options.appendWith + value;
				}
			}
		}

		// Json-encode object value
		if (typeof value === 'object') {
			value = JSON.stringify(value);
		}

		// @ts-ignore
		mpro.Cookies.set(name, value, options);
	},

	// Alias: setcookie
	setcookie(name, value, options) {
		return this.setCookie(name, value, options);
	},

	/**
	 * @param {ExtendedAjaxOptions} options
	 */
	remoteRequest(options) {

		if (!options) {
			throw new Error('mpro.actionHandlers.remoteRequest: Missing arguments');
		}

		if (!options.url) {
			throw new Error('mpro.actionHandlers.remoteRequest: Missing url');
		}

		// Make sure the response is as simple as possible, no unneeded parsing
		if (!options.dataType) {
			options.dataType = 'text';
		}

		doXhr(options.url, options);
	},

	// Alias: remote_request
	remote_request(options) {
		return this.remoteRequest(options);
	},

	// -----------------------------------------------------------
	// Shop action handlers
	// -----------------------------------------------------------

	// Reload the header section
	reload_header: () => {
		mpro.shop.section.reload(['header']);
	},

	// Reload the container section of the triggerElement
	reload_container: (args, actionType, res, triggerElement) => {
		// Use provided selector or the triggerElement
		mpro.shop.section.reloadContainer(args?.[0] ?? triggerElement);
	},

	// Reload the active modal container
	reload_modal: (args, actionType, res, triggerElement) => {
		// Use provided selector or the triggerElement
		mpro.shop.section.reloadContainer('div.modal.show div.modal-content');
	},

	// Reload the active drawer container
	reload_drawer: (args, actionType, res, triggerElement) => {
		// Use provided selector or the triggerElement
		mpro.shop.section.reloadContainer('div.offcanvas.show div.offcanvas-content');
	},

	// Reload the container section of the triggerElement
	reload_sections: (args) => {
		// @ts-ignore
		mpro.shop.section.reloadDelayed(...args);
	},

	// Refresh the whole page
	reload_page: () => {
		window.location.reload();
	},

	// Refresh the whole page if the cart is empty
	refresh_if_empty: (args, actionType, res, triggerElement) => {
		if (res?.data?.count_items === 0) {
			window.location.reload();
		}
	},

	// Remove the closest parent
	remove_closest: (args, actionType, res, triggerElement) => {
		const selector = args.shift();
		const options = args.shift() || {};
		const {
			animation = 'fadeOut',
			speed = 233,
		} = options;
		const $elem = $(triggerElement);
		$elem.closest(selector)[animation](speed, () => $elem.remove());
	},

	// Show cart confirmation in a toast notification
	toast_notification: (args, actionType, res, triggerElement) => {
		mpro.toast(parseToastOptions(res || args));
	},

	// Show message below the button
	inline_notification: (args, actionType, res, triggerElement) => {
		mpro.toast('info', 'TODO: implement \'inline_notification\' action');
	},

	// Close the modal that the element belongs to
	close_modal: () => {
		const overlay = mpro.getActiveModal();
		if (overlay) {
			overlay.hide();
		}
	},

	// Close the drawer that the element belongs to
	close_drawer: () => {
		const overlay = mpro.getActiveDrawer();
		if (overlay) {
			overlay.hide();
		}
	},

	// Close the drawer that the element belongs to
	close_overlay: () => {
		const overlay = mpro.getActiveModal() || mpro.getActiveDrawer();
		if (overlay) {
			overlay.hide();
		}
	},

	// -----------------------------------------------------------
	// Cart action handlers
	// -----------------------------------------------------------

	// Refresh cart section (counter + tooltip)
	update_cart_header: (args, actionType, res, triggerElement) => {
		if (!(res instanceof Error)) {
			const $button = $('[data-toggle-cart]');
			$button.attr('data-count', res?.data?.count_items || '');

			const {
				language,
				currency,
			} = mpro.config.env;

			$button.find('[data-total]').html(
				new Intl.NumberFormat(language.canonical_locale, {
					style: 'currency',
					currency: currency.iso_code,
				}).format(res?.data?.total_amount || 0),
			);
		}
	},

	// Redirect to /cart_preview
	redirect_cart: () => {
		mpro.setLocation(mpro.config.routes.cart);
	},

	// Redirect to /checkout
	redirect_checkout: () => {
		mpro.setLocation(mpro.config.routes.checkout);
	},

	// Show cart preview in a modal
	cart_preview_modal: ([modalOptions], actionType, res, triggerElement) => {

		// Close all modals before
		mpro.closeAllModals();

		showCartModal({
			...modalOptions,
			url: appendTimeToUrl(mpro.config.routes.cart_preview_modal),
			relatedTarget: triggerElement,
		});
	},

	// Show last added item in a modal
	last_item_modal: ([modalOptions], actionType, res, triggerElement) => {

		// Close all modals before
		mpro.closeAllModals();

		let url = mpro.config.routes.last_item_modal;

		// Append last ids added to cart to the modal url
		const parsedUrl = new URL(url);
		(res.data.added_items ?? []).map(obj => obj.cart_id).map((item, index) => parsedUrl.searchParams.append(`last_ids[${index}]`, item));
		url = parsedUrl.toString();

		showCartModal({
			...modalOptions,
			url: appendTimeToUrl(url),
			relatedTarget: triggerElement,
		});
	},

	// Show cart preview in a tooltip
	cart_preview_dropdown: () => {
		const $container = mpro.get$B();
		const $cartButtons = $container.find('[data-toggle-cart]');

		// Close all modals before
		mpro.closeAllModals();

		for (const cartButton of $cartButtons) {
			// Get the tippy container
			const $menuContainer = $container.find('[data-menu-container]').length > 0 ? $container.find('[data-menu-container]') : $container.find('#header');
			// Init the dropdown
			cartDropdownInit($menuContainer, cartButton, {}).then(instance => instance.show());
		}
	},

	// Show cart preview in side drawer
	cart_preview_drawer: ([drawerOptions], actionType, res, triggerElement) => {
		// Close all modals before
		mpro.closeAllModals();

		showCartDrawer({
			...drawerOptions,
			url: appendTimeToUrl(mpro.config.routes.cart_preview_drawer),
			relatedTarget: triggerElement,
		});
	},

	// Show last added item in side drawer
	last_item_drawer: ([drawerOptions], actionType, res, triggerElement) => {
		// Close all modals before
		mpro.closeAllModals();

		let url = mpro.config.routes.last_item_drawer;

		// Append last ids added to cart to the modal url
		const parsedUrl = new URL(url);
		(res.data.added_items ?? []).map(obj => obj.cart_id).map((item, index) => parsedUrl.searchParams.append(`last_ids[${index}]`, item));
		url = parsedUrl.toString();

		showCartDrawer({
			...drawerOptions,
			url: appendTimeToUrl(url),
			relatedTarget: triggerElement,
		});
	},

	// Open a custom modal dialog
	show_modal: (args, actionType, res, triggerElement) => {
		mpro.showModal({
			...args[0],
			relatedTarget: triggerElement,
		});
	},

	update_google_consent: (args) => {
		window.gtag = window.gtag || function() {
			window.dataLayer.push(arguments);
		};
		window.gtag('consent', 'update', args);
		console.log('consent', args);
	},

	// Hide gift cart form
	hide_gift_card_form: (args, actionType, res, triggerElement) => {
		if (!(res instanceof Error)) {
			const $form = triggerElement.tagName === 'FORM' ? $(triggerElement) : $(triggerElement).closest('form');
			if ($form.length) {
				$form.find('[data-toggle=form]').prop('checked', false).attr('aria-expanded', 'true').triggerHandler('click');
			}
		}
	},
};

/**
 * @param {string} entityName
 * @param {string[]} actionPrefixes
 * @param {{ [key: string]: any; }} extraOptions
 *
 * @return {{ success: { desktop?: string[]; mobile?: string[]; }; error: { desktop?: string[]; mobile?: string[]; }; }}
 */
mpro.getOverrideActions = function(entityName, actionPrefixes, extraOptions) {

	const data = {
		success: {
			desktop: undefined,
			mobile: undefined,
		},
		error: {
			desktop: undefined,
			mobile: undefined,
		},
	};

	for (const actionPrefix of actionPrefixes) {

		const successActions = extraOptions[entityName + actionPrefix + 'Actions'];
		const successActionsMobile = extraOptions[entityName + actionPrefix + 'ActionsMobile'];
		const successCallback = extraOptions[entityName + actionPrefix + 'Callback'];
		const successCallbackMobile = extraOptions[entityName + actionPrefix + 'CallbackMobile'];

		// Get extra actions for all views
		let overrideActionsAll = (typeof successActions === 'string')
			? successActions.split(',').map(v => v.trim())
			: (Array.isArray(successActions) ? successActions : null);

		// Get custom action handler for desktop view
		let overrideActionsMobile = (typeof successActionsMobile === 'string')
			? successActionsMobile.split(',').map(v => v.trim())
			: (Array.isArray(successActionsMobile) ? successActionsMobile : overrideActionsAll);

		// Get custom action handler for all views
		overrideActionsAll = (typeof successCallback === 'string')
			? successCallback.split(',').map(v => v.trim())
			: (Array.isArray(successCallback) ? successCallback : overrideActionsAll);

		// Get custom action handler for mobile view
		overrideActionsMobile = (typeof successCallbackMobile === 'string')
			? successCallbackMobile.split(',').map(v => v.trim())
			: (Array.isArray(successCallbackMobile) ? successCallbackMobile : overrideActionsMobile);

		// Add action to data hash
		data[actionPrefix.toLowerCase()].desktop = overrideActionsAll;
		data[actionPrefix.toLowerCase()].mobile = overrideActionsMobile;
	}

	return data;
};

/**
 * @param {string} entityName
 * @param {{ [key: string]: function }} actions
 * @param {string} actionType
 * @param {HTMLElement} triggerElement
 * @param {*} res
 * @param {{ desktop?: string[]; mobile?: string[] }} overrideActions
 */
mpro.runSuccessHandlers = function(entityName, actions, actionType, res, triggerElement, overrideActions = {}) {

	const device = mpro.isMobile() ? 'mobile' : 'desktop';
	/** @type {MpDataAction[]} */
	const deviceActionNames = overrideActions[device] || mpro.config?.[entityName].actions?.[actionType]?.success?.[device] || [];

	if (deviceActionNames.length > 0) {
		for (const action of deviceActionNames) {

			// Get action name and arguments
			const [actionName, actionArgs] = parseDataAction(action);

			if (actions[actionName]) {

				try {
					actions[actionName](actionArgs, actionType, res, triggerElement);
				}
				catch (error) {
					console.warn('Error running %s action: %o', `"${entityName} ${actionType}"`, action, error);
				}
			}
			// Look for global function with the name of the defined action
			else if (typeof window[actionName] === 'function') {
				window[actionName](actionArgs, actionType, res, triggerElement);
			}
			else {
				console.warn('Invalid %s action: %o', `"${entityName} ${actionType}"`, action);
			}
		}
	}
};

/**
 * @param {string} entityName
 * @param {{ [key: string]: function }} actions
 * @param {string} actionType
 * @param {HTMLElement} triggerElement
 * @param {*} err
 * @param {{ desktop?: string[]; mobile?: string[] }} overrideActions
 */
mpro.runErrorHandlers = function(entityName, actions, actionType, err, triggerElement, overrideActions = {}) {

	const device = mpro.isMobile() ? 'mobile' : 'desktop';
	/** @type {MpDataAction[]} */
	const deviceActionNames = overrideActions[device] || mpro.config?.[entityName].actions?.[actionType]?.error?.[device] || [];
	const consoleMessage = `Error running the ${entityName} ${actionType} action`;

	let errorStatus;
	let errorType;
	let errorMessage;

	if (err && err.responseJSON) {
		errorStatus = err.responseJSON.status || 400;
		errorType = err.responseJSON.error || 'error_unknown';
		errorMessage = err.responseJSON.message || mpro.langvar('error_unknown');
	}
	else {
		errorStatus = 400;
		errorType = 'error_unknown';
		errorMessage = mpro.langvar('error_unknown');
	}

	// Create custom error
	const errRes = new ShopActionError({
		status: errorStatus,
		error: errorType,
		message: errorMessage,
	});

	// Debug error
	console.warn(consoleMessage + ': %o', err);

	if (deviceActionNames.length > 0) {
		for (const action of deviceActionNames) {

			// Get action name and arguments
			const [actionName, actionArgs] = parseDataAction(action);

			if (actions[actionName]) {
				try {
					actions[actionName](actionArgs, actionType, errRes, triggerElement);
				}
				catch (error) {
					console.warn('Error running %s action: %o', `"${entityName} ${actionType}"`, action, error);
				}
			}
			// Look for global function with the name of the defined action
			else if (typeof window[actionName] === 'function') {
				window[actionName](actionArgs, actionType, errRes, triggerElement);
			}
			else {
				console.warn('Invalid %s action: %o', `"${entityName} ${actionType}"`, action);
			}
		}
	}
	// Run the default error handler
	else {
		mpro.toast('error', errorMessage);
	}
};

export default mpro;
